import { BaseAnalytics } from '../baseAnalytics';
import type { PerformanceMetrics } from '@/types/analytics';
import { logger } from '@/utils/logger';
import { PerformanceNavigationTracker } from './performanceNavigation';
import { PerformanceInteractionTracker } from './performanceInteraction';
import { PerformanceResourceTracker } from './performanceResource';
import { PerformanceLongTaskTracker } from './performanceLongTask';
import { PerformanceObserverManager } from './performanceObserverManager';
import { PerformanceMetricsStore } from './performanceMetricsStore';
import { PerformancePaintTracker } from './performancePaintTracker';

/**
 * Core class for tracking performance metrics
 */
export class PerformanceCore extends BaseAnalytics {
  private hasInitialized: boolean = false;
  private metricsStore: PerformanceMetricsStore;
  private observerManager: PerformanceObserverManager;
  private navigationTracker: PerformanceNavigationTracker;
  private interactionTracker: PerformanceInteractionTracker;
  private resourceTracker: PerformanceResourceTracker;
  private longTaskTracker: PerformanceLongTaskTracker;
  private paintTracker: PerformancePaintTracker;

  constructor(consentManager?: { hasConsent: (type: any) => boolean }) {
    super(consentManager);
    
    this.metricsStore = new PerformanceMetricsStore();
    this.observerManager = new PerformanceObserverManager();
    this.navigationTracker = new PerformanceNavigationTracker(this, this.metricsStore);
    this.interactionTracker = new PerformanceInteractionTracker(this);
    this.resourceTracker = new PerformanceResourceTracker(this);
    this.longTaskTracker = new PerformanceLongTaskTracker(this);
    this.paintTracker = new PerformancePaintTracker(this, this.metricsStore);
    
    // Initialize on construction if we're in the browser
    if (typeof window !== 'undefined') {
      this.initializePerformanceTracking();
    }
  }
  
  initializePerformanceTracking() {
    if (this.hasInitialized || typeof window === 'undefined') {
      return; // Prevent multiple initializations or SSR errors
    }

    if (typeof PerformanceObserver !== 'undefined') {
      try {
        // Track page load timing first
        this.navigationTracker.trackNavigationTiming();
        
        // Initialize observers for all metrics
        this.observerManager.initializeObservers({
          onCoreWebVitals: this.trackPerformanceMetric.bind(this),
          onResource: this.resourceTracker.trackResourceTiming.bind(this),
          onInteraction: this.interactionTracker.trackInteractionTiming.bind(this),
          onPaint: this.paintTracker.trackPaintTiming.bind(this)
        });
        
        // Set flag to indicate initialization is complete
        this.hasInitialized = true;
        logger.performance('Performance tracking initialized');
      } catch (error) {
        logger.warn('Performance tracking not fully supported:', error);
      }
    }
  }

  trackPerformanceMetric(entry: PerformanceEntry) {
    // Store metrics for later use
    switch (entry.entryType) {
      case 'largest-contentful-paint':
        this.metricsStore.updateMetrics({ largestContentfulPaint: entry.startTime });
        break;
      case 'first-input':
        // Use type assertion to access first-input specific properties
        const firstInputEntry = entry as PerformanceEventTiming;
        this.metricsStore.updateMetrics({ 
          firstInputDelay: firstInputEntry.processingStart ? 
                          firstInputEntry.processingStart - firstInputEntry.startTime : 
                          undefined
        });
        break;
      case 'layout-shift':
        // For layout shift entries, we need to use a type assertion and access the value property
        const layoutShiftEntry = entry as any; // Using any as LayoutShift is not widely supported in TypeScript definitions
        this.metricsStore.addCumulativeLayoutShift(layoutShiftEntry.value || 0);
        break;
      case 'longtask':
        this.longTaskTracker.trackLongTask(entry);
        break;
    }
    
    this.trackPerformanceEvent({
      category: 'performance', // Add the required category property
      action: 'performance_metric',
      label: entry.entryType,
      value: Math.round(entry.startTime),
      metadata: {
        metric: entry.entryType,
        value: entry.startTime,
        ...this.getEntryProperties(entry)
      }
    });
  }
  
  getCurrentMetrics(): PerformanceMetrics {
    // Update CLS before returning since it's cumulative
    if (typeof window !== 'undefined' && this.hasInitialized) {
      // Refresh navigation timing data
      this.navigationTracker.trackNavigationTiming();
    }
    
    return this.metricsStore.getAllMetrics();
  }
  
  // Helper function to safely extract properties from the performance entry
  getEntryProperties(entry: PerformanceEntry): Record<string, any> {
    try {
      return JSON.parse(JSON.stringify(entry));
    } catch {
      return {
        name: entry.name,
        entryType: entry.entryType,
        startTime: entry.startTime,
        duration: entry.duration
      };
    }
  }
  
  // Clean up all observers
  public disconnect() {
    this.observerManager.disconnectAll();
  }

  // Update metrics from other trackers (public for tracker classes)
  updateMetrics(updatedMetrics: Partial<PerformanceMetrics>) {
    this.metricsStore.updateMetrics(updatedMetrics);
  }
}
